import { Injectable, NgZone } from '@angular/core';
import {
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  map,
  merge,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { enterZone } from '../utilities/ZoneUtils';
import { PanelDirection, PanelView } from '../sessions/panel/panel.component';
import { SessionSharedDataService } from './session-shared-data.service';
import {
  MIN_WIDTH_NEEDED_FOR_LEFT_SECTION_BOTTOM_TOOLBAR_V2,
  ScreenSizeService,
} from './screen-size.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class PanelLayoutService {
  // TODO: @bouidia
  // move related right / left panel logic to this service

  private _leftPanelView: PanelView | undefined;
  private _rightPanelView: PanelView | undefined;

  private autoCloseBottomSheetSubscription?: Subscription;
  private canvasMinWidthReachedSubscription?: Subscription;

  constructor(
    private sessionSharedDataService: SessionSharedDataService,
    private screenSizeService: ScreenSizeService,
    private ngZone: NgZone,
  ) {}

  readonly isUploadPanelOpen$ = this.checkIfPanelIsOpen$(PanelView.uploadFile);

  readonly isAnyPanelOpen$ = combineLatest([
    this.sessionSharedDataService.leftPanelView.asObservable(),
    this.sessionSharedDataService.rightPanelView.asObservable(),
  ]).pipe(
    map(([leftPanelState, rightPanelState]) =>
      Boolean(leftPanelState?.panelView || rightPanelState?.panelView),
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  // Emits whenever anything that affects a panel's bottom-sheet rendering changes: which panel is
  // open on each side, plus the available-width breakpoints. Shared by the aggregate and per-side
  // streams below so the trigger set is declared once.
  private readonly bottomSheetInputs$ = merge(
    this.isUploadPanelOpen$,
    this.sessionSharedDataService.leftPanelView.asObservable().pipe(
      map((state) => state?.panelView),
      distinctUntilChanged(),
    ),
    this.sessionSharedDataService.rightPanelView.asObservable().pipe(
      map((state) => state?.panelView),
      distinctUntilChanged(),
    ),
    this.screenSizeService.isUploadPanelBreakpointReached$,
    this.screenSizeService.isPanelsBottomSheetBreakpointReached$,
    this.screenSizeService.isSystemStatusPanelBreakpointReached$,
    this.screenSizeService.isWidePanelBreakpointReached$,
  );

  readonly isAnyPanelRenderedAsBottomSheet$ = this.bottomSheetInputs$.pipe(
    map(() => this.anyPanelOpenedAsBottomSheet),
    distinctUntilChanged(),
  );

  // Per-side variants so each panel element can be styled as a bottom sheet from this single source
  // (the CSS reads these instead of re-deriving the thresholds via its own media queries).
  readonly isLeftPanelRenderedAsBottomSheet$ = this.bottomSheetInputs$.pipe(
    map(() => this.isLeftPanelRenderedAsBottomSheet),
    distinctUntilChanged(),
  );

  readonly isRightPanelRenderedAsBottomSheet$ = this.bottomSheetInputs$.pipe(
    map(() => this.isRightPanelRenderedAsBottomSheet),
    distinctUntilChanged(),
  );

  readonly isSystemStatusPanelOpen$ = this.checkIfPanelIsOpen$(PanelView.systemStatus);
  readonly isWideLeftPanelOpen$ = this.checkIfPanelIsOpen$(
    PanelView.lhsQuestions,
    PanelView.phetSimulations,
    PanelView.bioDigitalModels,
  );

  get anyPanelOpenedAsBottomSheet(): boolean {
    return this.isLeftPanelRenderedAsBottomSheet || this.isRightPanelRenderedAsBottomSheet;
  }

  // The left panel becomes a bottom sheet either at the shared panels breakpoint (when open) or at
  // its content-specific breakpoint (upload / wide panels).
  get isLeftPanelRenderedAsBottomSheet(): boolean {
    return (
      Boolean(this._leftPanelView) &&
      (this.screenSizeService.isPanelsBottomSheetBreakpointReached ||
        this.isUploadPanelRenderedAsBottomSheet ||
        this.isWideLeftPanelRenderedAsBottomSheet)
    );
  }

  // The right panel becomes a bottom sheet at the shared panels breakpoint (when open) or at the
  // system-status breakpoint.
  get isRightPanelRenderedAsBottomSheet(): boolean {
    if (this._rightPanelView === PanelView.aiTutor) {
      return false;
    }
    return (
      Boolean(this._rightPanelView) &&
      (this.screenSizeService.isPanelsBottomSheetBreakpointReached ||
        this.isSystemStatusPanelRenderedAsBottomSheet)
    );
  }

  get isWideLeftPanelRenderedAsBottomSheet(): boolean {
    return Boolean(
      this._leftPanelView &&
        [PanelView.lhsQuestions, PanelView.phetSimulations, PanelView.bioDigitalModels].includes(
          this._leftPanelView,
        ) &&
        this.screenSizeService.isWidePanelBreakpointReached,
    );
  }

  get isUploadPanelRenderedAsBottomSheet(): boolean {
    return (
      this._leftPanelView === PanelView.uploadFile &&
      this.screenSizeService.isUploadPanelBreakpointReached
    );
  }

  get isSystemStatusPanelRenderedAsBottomSheet(): boolean {
    return (
      this._rightPanelView === PanelView.systemStatus &&
      this.screenSizeService.isSystemStatusPanelBreakpointReached
    );
  }

  get leftPanelView() {
    return this.sessionSharedDataService.leftPanelView;
  }

  get rightPanelView() {
    return this.sessionSharedDataService.rightPanelView;
  }

  get isRightPanelViewOpen(): boolean {
    return Boolean(this._rightPanelView);
  }

  get isLeftPanelViewOpen(): boolean {
    return Boolean(this._leftPanelView);
  }

  get isAnyPanelOpen(): boolean {
    return this.isLeftPanelViewOpen || this.isRightPanelViewOpen;
  }

  initializeAutoCloseBottomSheet(): void {
    let lastPanelOpened: PanelDirection | undefined;
    // auto close bottom sheet left/right panels on canvas click
    // Flow explanation:
    // - We are watching both the left and right panel states concurrently.
    // - When either the left or right panel is opened (i.e., has a panelView and is expanded),
    //   we remember which side was last opened and ensure the opposite panel's bottom sheet is closed.
    // - combineLatest emits whenever either panel changes, so we know if any panel is currently open.
    // - If any panel is open, we subscribe to canvas mouse down events so a click outside (on the canvas)
    //   will close all open panel bottom sheets.
    // - If no panel is open, we unsubscribe from canvas mouse down events for cleanup.
    this.autoCloseBottomSheetSubscription = combineLatest([
      this.leftPanelView.asObservable().pipe(
        tap((state) => {
          if (state?.panelView && state.expanded) {
            lastPanelOpened = PanelDirection.LEFT;
            this.closeOppositePanelForBottomSheet(PanelDirection.LEFT);
          }
        }),
        map((state) => Boolean(state?.panelView && state?.expanded)),
      ),
      this.rightPanelView.asObservable().pipe(
        tap((state) => {
          if (state?.panelView && state.expanded) {
            lastPanelOpened = PanelDirection.RIGHT;
            this.closeOppositePanelForBottomSheet(PanelDirection.RIGHT);
          }
        }),
        map((state) => Boolean(state?.panelView && state?.expanded)),
      ),
    ])
      .pipe(
        map(([isLeftPanelOpen, isRightPanelOpen]) => isLeftPanelOpen || isRightPanelOpen),
        distinctUntilChanged(),
        untilDestroyed(this),
        switchMap((isAnyPanelOpen) =>
          isAnyPanelOpen && this.sessionSharedDataService.fabricCanvas?.onMouseDown$
            ? this.sessionSharedDataService.fabricCanvas?.onMouseDown$.pipe(enterZone(this.ngZone))
            : EMPTY,
        ),
      )
      .subscribe(() => this.closePanelBottomSheet());

    // if the canvas width is less than the minimum width needed for the left section bottom toolbar v2
    // close the previous opened panel
    this.canvasMinWidthReachedSubscription = this.screenSizeService.currentSpaceCanvasWidth$
      .pipe(map((width) => width <= MIN_WIDTH_NEEDED_FOR_LEFT_SECTION_BOTTOM_TOOLBAR_V2))
      .pipe(untilDestroyed(this))
      .subscribe((canvasMinWidthReached) => {
        if (!canvasMinWidthReached) {
          return;
        }

        if (lastPanelOpened === PanelDirection.LEFT) {
          this.sessionSharedDataService.changeRightPanelView.next(undefined);
        } else if (lastPanelOpened === PanelDirection.RIGHT) {
          this.sessionSharedDataService.changeLeftPanelView.next(undefined);
        }
      });
  }

  checkIfPanelIsOpen$(...panels: PanelView[]): Observable<boolean> {
    return combineLatest([
      this.sessionSharedDataService.leftPanelView.asObservable().pipe(
        map((state) => Boolean(state?.panelView && panels.includes(state?.panelView))),
        distinctUntilChanged(),
      ),
      this.sessionSharedDataService.rightPanelView.asObservable().pipe(
        map((state) => Boolean(state?.panelView && panels.includes(state?.panelView))),
        distinctUntilChanged(),
      ),
    ]).pipe(
      map(([isLeftPanelOpen, isRightPanelOpen]) => isLeftPanelOpen || isRightPanelOpen),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  setLeftPanelView(panelView: PanelView | undefined): void {
    this._leftPanelView = panelView;
  }

  setRightPanelView(panelView: PanelView | undefined): void {
    this._rightPanelView = panelView;
  }

  /**
   * A bottom sheet spans the full width, so whatever is open on the other side ends up underneath
   * it. Keyed off the opening panel's own render mode rather than the shared breakpoint: the
   * content-specific breakpoints (upload, wide panels, system status) turn one side into a sheet
   * while the other is still a side panel, and that side panel has to close too.
   */
  private closeOppositePanelForBottomSheet(openedSide: PanelDirection): void {
    if (openedSide === PanelDirection.LEFT && this.isLeftPanelRenderedAsBottomSheet) {
      this.sessionSharedDataService.changeRightPanelView.next(undefined);
      return;
    }

    if (openedSide === PanelDirection.RIGHT && this.isRightPanelRenderedAsBottomSheet) {
      this.sessionSharedDataService.changeLeftPanelView.next(undefined);
    }
  }

  closePanelBottomSheet(): void {
    if (this.anyPanelOpenedAsBottomSheet) {
      this.sessionSharedDataService.changeLeftPanelView.next(undefined);
      this.sessionSharedDataService.changeRightPanelView.next(undefined);
    }
  }

  uninitializeAutoCloseBottomSheet(): void {
    this.autoCloseBottomSheetSubscription?.unsubscribe();
    this.canvasMinWidthReachedSubscription?.unsubscribe();
  }

  getVisibleCanvasWidth(rawCanvasWidth: number): number {
    // Global sidebar in switcher mode reduces the visible width like a panel (0 otherwise).
    const sidebarWidth = this.sessionSharedDataService.getPanelWidth().sidebarWidth;

    const visibleCanvasWidth = rawCanvasWidth - sidebarWidth;

    // Bottom-sheet panels overlay the canvas without shrinking the visible width
    if (this.anyPanelOpenedAsBottomSheet) {
      return visibleCanvasWidth;
    }

    // The canvas stays window-sized; side panels clip the visible area.
    const { leftPanelWidth, rightPanelWidth } = this.sessionSharedDataService.getPanelWidth();
    return Math.max(0, visibleCanvasWidth - leftPanelWidth - rightPanelWidth);
  }
}
